import classNames from "classnames";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  formatTimeAfterDate,
  stringToDateAndTime,
} from "@/components/helpers/DateFormatters";
import Loading from "@/components/Loading";
import NotificationsCenterNavigation from "@/components/settings/notificationsCenter/NotificationsCenterNavigation";
import SingleTask from "@/components/settings/notificationsCenter/tasks/SingleTask";
import { useMainStore } from "@/contexts/Store";

type FilterID =
  | "all_tasks"
  | "due_today"
  | "overdue"
  | "upcoming"
  | "completed";

type AvailableFilter = {
  id: FilterID;
  filterName: string;
};

const availableFilters: AvailableFilter[] = [
  {
    id: "all_tasks",
    filterName: "All Tasks",
  },
  {
    id: "due_today",
    filterName: "Due today",
  },
  {
    id: "overdue",
    filterName: "Overdue",
  },
  {
    id: "upcoming",
    filterName: "Upcoming",
  },
  {
    id: "completed",
    filterName: "Completed",
  },
];

function AllTasks() {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [workspaceID, setWorkspaceID] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState<FilterID>("all_tasks");
  const [selectedTaskID, setSelectedTaskID] = useState<number | null>(null);

  // Hooks
  const { task_id } = useParams<{ task_id: string }>();
  const taskID = Number(task_id);
  const location = useLocation();

  useEffect(() => {
    if (selectedFilter) {
      setSelectedTaskID(null);
    }
  }, [selectedFilter]);

  useEffect(() => {
    const anchor = location.hash;
    if (anchor === "#completed") {
      setSelectedFilter("completed");
    } else if (anchor === "#upcoming") {
      setSelectedFilter("upcoming");
    } else if (anchor === "#overdue") {
      setSelectedFilter("overdue");
    } else if (anchor === "#due_today") {
      setSelectedFilter("due_today");
    }
  }, [location]);

  useEffect(() => {
    if (!mainStore.tasks.allTasks) {
      mainStore.tasks.myTasks();
    }

    return () => {
      mainStore.tasks.cleanup();
    };
  }, []);

  // Variables
  const today = formatTimeAfterDate(new Date());

  if (!mainStore.tasks.allTasks) {
    return <Loading loadingLayout="table" showTableHeader />;
  }

  const allActiveTasks = mainStore.tasks.allTasks.filter(
    (task) => !task.resolved,
  );
  const dueDateTasks = allActiveTasks.filter((task) => {
    const dueDate = momentDateTime(task.due_date);
    return (
      dueDate.isSame(today, "day") &&
      (dueDate.isAfter(today) || dueDate.isSame(today))
    );
  });
  const overdueTasks = allActiveTasks.filter((task) => {
    const dueDate = momentDateTime(task.due_date);
    return (
      dueDate.isBefore(today, "day") ||
      (dueDate.isSame(today, "day") && dueDate.isBefore(today))
    );
  });
  const upcomingTasks = allActiveTasks.filter((task) =>
    momentDateTime(task.due_date).isAfter(today, "day"),
  );
  const completedTasks = mainStore.tasks.allTasks.filter(
    (task) => task.resolved,
  );

  const listTasks = getListTasks();

  // Functions
  function getListTasks() {
    switch (selectedFilter) {
      case "all_tasks":
        return allActiveTasks;
      case "due_today":
        return dueDateTasks;
      case "overdue":
        return overdueTasks;
      case "upcoming":
        return upcomingTasks;
      case "completed":
        return completedTasks;
      default:
        return allActiveTasks;
    }
  }

  function momentDateTime(date: string) {
    return moment(formatTimeAfterDate(stringToDateAndTime(date)));
  }

  const listTasksClasses = classNames("filter-task-wrap", {
    "filter-task-addendum": listTasks.length === 0,
  });

  // Functions
  function idSelection(id: number) {
    setWorkspaceID(id);
  }

  function filterSelection(id: FilterID) {
    setSelectedFilter(id);
  }

  return (
    <>
      <NotificationsCenterNavigation
        idSelection={idSelection}
        workspaceID={workspaceID}
      />
      <div className="all-tasks-wrap">
        <div className={listTasksClasses}>
          <ul>
            {availableFilters.map((filter) => {
              const filterClasses = classNames({
                active: filter.id === selectedFilter,
              });
              const taskListClasses = classNames({
                "overdue-task":
                  filter.id === "due_today" || filter.id === "overdue",
              });
              return (
                <li
                  key={filter.id}
                  className={filterClasses}
                  onClick={() => {
                    filterSelection(filter.id);
                  }}
                >
                  {filter.filterName}
                  {filter.id === "all_tasks" &&
                    (selectedFilter === "all_tasks" ||
                      allActiveTasks.length > 0) && (
                      <span className={taskListClasses}>
                        {allActiveTasks.length}
                      </span>
                    )}
                  {filter.id === "due_today" &&
                    (selectedFilter === "due_today" ||
                      dueDateTasks.length > 0) && (
                      <span className={taskListClasses}>
                        {dueDateTasks.length}
                      </span>
                    )}
                  {filter.id === "overdue" &&
                    (selectedFilter === "overdue" ||
                      overdueTasks.length > 0) && (
                      <span className={taskListClasses}>
                        {overdueTasks.length}
                      </span>
                    )}
                  {filter.id === "upcoming" &&
                    (selectedFilter === "upcoming" ||
                      upcomingTasks.length > 0) && (
                      <span className={taskListClasses}>
                        {upcomingTasks.length}
                      </span>
                    )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="all-tasks-block">
          {listTasks.length > 0 ? (
            listTasks.map((task, index) => {
              const tasksClasses = classNames("element-task-wrap", {
                "element-task-position": task.id == taskID,
              });
              return (
                <div key={task.id} className={tasksClasses}>
                  <SingleTask
                    task={task}
                    workspaceID={workspaceID}
                    selectedTaskID={selectedTaskID}
                    setSelectedTaskID={setSelectedTaskID}
                    opened={index == 0}
                  />
                </div>
              );
            })
          ) : (
            <div className="no-task-wrap">
              <h3>No relevant tasks</h3>
              <p>
                Seems like there are no relevant tasks with the assignees you’ve
                selected. Please modify your selections.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default observer(AllTasks);
